<template>
  <div
    v-if="amountVerificationData.length > 0"
    class="amount-verification-section"
  >
    <h4 class="section-title">合計金額の確認</h4>

    <div class="amount-cards-container">
      <el-card
        v-for="data in amountVerificationData"
        :key="data.docIndex"
        class="amount-card"
        :class="{ 'has-difference': data.difference.hasDifference }"
      >
        <div class="card-header">
          <strong>{{ data.docLabel }}</strong>
        </div>

        <div class="card-body">
          <div class="amount-row">
            <span class="label">ヘッダー項目の合計金額:</span>
            <span class="value">{{ data.totalAmount || '-' }}{{ data.currency ? ' ' + data.currency : '' }}</span>
          </div>

          <div class="amount-row">
            <span class="label">明細項目の金額の和:</span>
            <span class="value">{{ formatItemsSum(data.itemsSum) }}</span>
          </div>

          <div v-if="data.itemsAmounts.length > 0" class="amount-row formula-row">
            <span class="formula text-10">{{ formatItemsFormula(data.itemsAmounts) }}</span>
          </div>

          <div class="amount-row difference-row">
            <span class="label">差分:</span>
            <span
              class="value"
              :class="{ 'has-diff': data.difference.hasDifference }"
            >
              {{ data.difference.text }}
            </span>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CrossCheckAmountVerification',
  props: {
    selectedDocuments: {
      type: Array,
      required: true,
      default: () => [],
    },
    compareData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  computed: {
    docLabels() {
      const counts = {};
      this.selectedDocuments.forEach((doc) => {
        const cat = doc.documentCategory || '';
        counts[cat] = (counts[cat] || 0) + 1;
      });
      const indices = {};
      return this.selectedDocuments.map((doc, i) => {
        const cat = doc.documentCategory || `書類${i + 1}`;
        if (counts[cat] > 1) {
          indices[cat] = (indices[cat] || 0) + 1;
          return `${cat}-${indices[cat]}`;
        }
        return cat;
      });
    },
    amountVerificationData() {
      const documents = this.compareData?.documents || [];

      return documents.map((doc, index) => {
        // ヘッダー項目から合計金額を取得（exCustoms が見つからない場合 imCustoms をフォールバック）
        const headerItems = doc.ocrResults?.aiocrItems || [];
        const totalAmountItem = headerItems.find(
          (i) =>
            i.twFieldName === 'exCustomsTotalAmount' ||
            i.twFieldName === 'imCustomsTotalAmount',
        );
        const totalAmount = totalAmountItem?.ocrFieldValue || '';

        const currencyItem = headerItems.find(
          (i) =>
            i.twFieldName === 'exCustomsCurrency' ||
            i.twFieldName === 'imCustomsCurrency',
        );
        const currency = currencyItem?.ocrFieldValue || '';

        // 明細の金額を合計
        const items = doc.ocrResults?.aiocrTableItems?.goodsLine || [];
        const itemsSum = this.calculateItemsAmountSum(items);

        // 明細の各金額をリストとして保持（計算式表示用）
        const itemsAmounts = items
          .map((item) => parseFloat(item.ocrAmountPerItem || 0))
          .filter((amount) => amount > 0);

        // 差分を計算
        const difference = this.calculateAmountDifference(
          totalAmount,
          itemsSum,
          currency,
        );

        return {
          docIndex: index + 1,
          docLabel: this.docLabels[index] || `書類${index + 1}`,
          documentName: this.selectedDocuments[index]?.documentName || '',
          totalAmount,
          currency,
          itemsSum,
          itemsAmounts,
          difference,
        };
      });
    },
  },
  methods: {
    /**
     * 明細の金額を単位別に合計
     */
    calculateItemsAmountSum(items) {
      const amountByCurrency = {};

      items.forEach((item) => {
        const amount = parseFloat(item.ocrAmountPerItem || 0);
        const currency =
          item.ocrImCustomsCurrencies || item.ocrCurrency || '';

        if (amount > 0) {
          if (!amountByCurrency[currency]) {
            amountByCurrency[currency] = 0;
          }
          amountByCurrency[currency] += amount;
        }
      });

      return amountByCurrency;
    },

    /**
     * 差分を計算（2つの書類比較と同じ算出式）
     */
    calculateAmountDifference(totalAmount, itemsSum, currency) {
      // 明細の通貨が複数、またはヘッダー通貨と異なる場合はチェック不可
      const itemsCurrencies = Object.keys(itemsSum).filter(
        (c) => itemsSum[c] > 0,
      );
      if (
        itemsCurrencies.length > 1 ||
        (itemsCurrencies.length === 1 && itemsCurrencies[0] !== currency)
      ) {
        return {
          text: '単位通貨が異なるため、チェックしません',
          hasDifference: false,
        };
      }

      const total = parseFloat(String(totalAmount).replace(/,/g, ''));

      // 合計金額が数値として解釈できない場合はチェック不可
      if (Number.isNaN(total)) {
        return {
          text: '合計金額の形式が読み取れないため、チェックしません',
          hasDifference: false,
        };
      }

      // itemsSumから該当通貨の金額を取得
      const sum = itemsSum[currency] || 0;

      const diff = total - sum;

      // 差分が0.01未満なら「なし」
      if (Math.abs(diff) < 0.01) {
        return { text: 'なし', hasDifference: false };
      }

      // 差分割合を計算（2つの書類比較と同じ式）
      const diffPercent =
        total !== 0 ? ((diff / total) * 100).toFixed(1) : '0.0';

      // 2つの書類比較と同じフォーマット（toLocaleString使用、全角括弧）
      const result = {
        text: `${diff.toLocaleString()} ${currency}（${diffPercent}%）`,
        hasDifference: true,
        amount: diff,
        percent: diffPercent,
      };

      return result;
    },

    /**
     * 明細の和をフォーマット（複数通貨の場合は数式形式）
     */
    formatItemsSum(itemsSum) {
      const parts = Object.entries(itemsSum)
        .filter(([, amount]) => amount > 0)
        .map(([currency, amount]) => `${amount.toFixed(2)} ${currency}`);

      return parts.length > 0 ? parts.join(' + ') : '-';
    },

    /**
     * 明細の計算式を生成（例: ( 34950 + 35400 )）
     */
    formatItemsFormula(amounts) {
      if (amounts.length === 0) return '';
      const formatted = amounts.map((a) => a.toFixed(2)).join(' + ');
      return `( ${formatted} )`;
    },
  },
};
</script>

<style scoped lang="scss">
.amount-verification-section {
  margin-top: 20px;
  padding: 15px;
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
}

.section-title {
  margin: 0 0 15px 0;
  font-size: 14px;
  font-weight: bold;
  color: #303133;
}

.amount-cards-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.amount-card {
  width: calc(25% - 11.25px);
  min-width: 280px;

  &.has-difference {
    border: 1px solid #f56c6c;
  }
}

.card-header {
  font-size: 13px;
  font-weight: bold;
  color: #606266;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4e7ed;
}

.card-body {
  .amount-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 12px;

    .label {
      color: #606266;
      font-weight: 500;
    }

    .value {
      color: #303133;
      font-weight: bold;
      text-align: right;
      word-break: break-word;
      white-space: normal;
    }

    &.formula-row {
      justify-content: flex-end;
      margin-top: 4px;
      margin-bottom: 0;

      .formula {
        color: #909399;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
        text-align: right;
      }
    }

    &.difference-row {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #f0f0f0;

      .value.has-diff {
        color: #f56c6c;
      }
    }
  }
}
</style>
